import axios from '../helpers/axiosConfig';
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../helpers/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import getBaseUrl from '../helpers/utility';

const PersonnelsMilitaireRetraite = () => {
  const baseUrl = getBaseUrl();

  const navigate = useNavigate();
  const [personnels, setPersonnels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPersonnels = async () => {
      toast.info('Chargement en cours ...');
      const token = localStorage.getItem('accessToken');

      try {
        const response = await axios.get('/personnel', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.data;

        const today = new Date();
        const yearInTwoYears = today.getFullYear();

        const personnelsRetraite = data.personnels.filter((personnel) => {
          if (personnel.typePers === 'MILITAIRE') {
            if (personnel.datenaisse) {
              const birthDate = new Date(personnel.datenaisse);
              const retirementAge = personnel.grade.anneeretraite;

              if (!retirementAge) {
                console.log(`Retraite manquante pour ${personnel.nom}`);
                return false;  // Exclure ce personnel si l'âge de retraite est manquant
              }

              const retirementYear = birthDate.getFullYear() + retirementAge;

              // Vérifier les années de retraite et appliquer le filtrage
              //console.log(`Nom: ${personnel.nomprenom}, Année de retraite: ${retirementYear}, Aujourd'hui: ${today.getFullYear()}, 2 ans: ${yearInTwoYears}`);

              // Filtrage des personnels dans les 2 prochaines années
              return retirementYear >= today.getFullYear() && retirementYear <= yearInTwoYears;
            }
          }
          return false;  // Si ce n'est pas un personnel militaire ou sans date de naissance
        });

        // Trier les personnels par année de retraite en ordre croissant
        personnelsRetraite.sort((a, b) => {
          const retirementYearA = new Date(a.datenaisse).getFullYear() + a.grade.anneeretraite;
          const retirementYearB = new Date(b.datenaisse).getFullYear() + b.grade.anneeretraite;
          return retirementYearA - retirementYearB; // Tri par année de retraite en ordre croissant
        });

        setPersonnels(personnelsRetraite);
      } catch (err) {
        toast.error(err.message);
      }
      setLoading(false);
    };

    fetchPersonnels();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 mb-12">
      {personnels.length === 0 ? (
        /* Loader épuré */
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm">
          <div className="w-10 h-10 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin"></div>
          <p className="mt-4 text-sm font-medium text-slate-500">Chargement des données...</p>
        </div>
      ) : (
        <div className="space-y-6">

          {/* En-tête de la section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                Retraités dans les années suivantes
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                Liste prévisionnelle du personnel arrivant à l'âge limite.
              </p>
            </div>

            {/* Badge de résultats discret et moderne */}
            {personnels && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold self-start sm:self-center">
                <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                {personnels.length} {personnels.length > 1 ? 'résultats trouvés' : 'résultat trouvé'}
              </div>
            )}
          </div>

          {/* Conteneur du Tableau avec scroll horizontal fluide sur mobile */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-100/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">

                {/* En-tête du tableau */}
                <thead>
                  <tr className="bg-slate-50/75 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wider">
                    <th className="py-4 px-6">Photo</th>
                    <th className="py-4 px-6">Grade</th>
                    <th className="py-4 px-6">Nom et prénoms</th>
                    <th className="py-4 px-6">Date de naissance</th>
                    <th className="py-4 px-6">Ets. / Sce / Compagnie</th>
                    <th className="py-4 px-6">Date retraite</th>
                    <th className="py-4 px-6 text-center">Action</th>
                  </tr>
                </thead>

                {/* Corps du tableau */}
                <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
                  {personnels.map((personnel) => (
                    <tr
                      key={personnel.id}
                      className="hover:bg-slate-50/50 transition-colors duration-200 group"
                    >
                      {/* Photo de profil */}
                      <td className="py-3.5 px-6 whitespace-nowrap">
                        <div className="flex items-center">
                          <img
                            src={`${baseUrl}/images/personnels/${personnel.filename}`}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-100 group-hover:ring-indigo-100 transition-all bg-slate-100"
                            alt=""
                          />
                        </div>
                      </td>

                      {/* Grade */}
                      <td className="py-3.5 px-6 whitespace-nowrap font-medium text-slate-900">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-800">
                          {personnel.grade.name}
                        </span>
                      </td>

                      {/* Nom & prénoms */}
                      <td className="py-3.5 px-6 font-semibold text-slate-800 group-hover:text-indigo-600 transition-colors">
                        {personnel.nomprenom}
                      </td>

                      {/* Date de naissance */}
                      <td className="py-3.5 px-6 whitespace-nowrap text-slate-500">
                        {personnel.datenaisse}
                      </td>

                      {/* Structure / Service */}
                      <td className="py-3.5 px-6 text-slate-500 max-w-xs truncate">
                        <div className="font-medium text-slate-700">
                          {personnel.etsouservice && personnel.etsouservice.name}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {personnel.compagnie && personnel.compagnie.name}
                        </div>
                      </td>

                      {/* Date Retraite calculée */}
                      <td className="py-3.5 px-6 whitespace-nowrap font-bold text-amber-600">
                        {new Date(personnel.datenaisse).getFullYear() + personnel.grade.anneeretraite}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-6 whitespace-nowrap text-center">
                        <button
                          onClick={() => navigate(`/personnel/${personnel.id}`)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200 active:scale-95"
                          title="Voir la fiche"
                        >
                          <i className="fa fa-eye text-base"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default PersonnelsMilitaireRetraite;
